/** CONSULTING QUOTES — /admin/quotes. TravelTrucks Studio treatment: aurora glass,
 * spectrum accents, Poppins/Inter. AI line-item requests are ADDITIVE ops
 * (add/update/remove) — existing items are never clobbered; every AI change
 * is undoable. */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Chrome, C } from './V2AdminOSPage';

const API = process.env.REACT_APP_BACKEND_URL;
const req = async (method, path, body, isForm = false) => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('kindra-admin-token') || ''}` };
  if (!isForm) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${API}${path}`, { method, headers, body: isForm ? body : (body ? JSON.stringify(body) : undefined) });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.detail || `HTTP ${res.status}`);
  return d;
};

const SPECTRUM = C.spectrum;
const ACCENTS = ['#34D1B0', '#1FB6C1', '#4F8FD0', '#9156C4', '#EB258D'];
const card = { position: 'relative', background: 'rgba(26,15,68,0.55)', border: `1px solid ${C.hair}`, borderRadius: 18, padding: '20px 22px', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' };
const inp = { width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: 12, border: `1px solid ${C.hair}`, background: 'rgba(220,240,245,0.04)', color: C.icy, fontFamily: 'Inter', fontSize: 13.5, outline: 'none', transition: 'border-color 160ms ease' };
const eyebrow = { display: 'block', fontFamily: 'Inter', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.faint, margin: '0 0 10px' };
const hFont = { fontFamily: 'Poppins, Inter', fontWeight: 600, color: C.icy };
const btnPrimary = { padding: '11px 24px', borderRadius: 999, border: 'none', backgroundImage: SPECTRUM, color: '#fff', fontFamily: 'Inter', fontSize: 13, fontWeight: 700, letterSpacing: '0.02em', cursor: 'pointer', boxShadow: '0 8px 24px rgba(235,37,141,0.25)', transition: 'transform 160ms ease, box-shadow 160ms ease' };
const ghost = { padding: '9px 16px', borderRadius: 999, border: `1px solid ${C.hairBright}`, background: 'transparent', color: C.icy, fontFamily: 'Inter', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'border-color 160ms ease, background 160ms ease' };
const STATUS_C = { draft: C.faint, approved: '#9156C4', sent: '#4F8FD0', viewed: '#1FB6C1', accepted: '#34D1B0', declined: '#EB258D', expired: '#EB258D',
                    invoice_draft: '#F0C36D', invoice_sent: '#F0C36D', invoice_paid: '#34D1B0', invoice_voided: '#EB258D' };
const chip = (c) => ({ fontFamily: 'Inter', fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: c, border: `1px solid ${c}55`, background: `${c}10`, borderRadius: 999, padding: '3px 10px' });
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const CQStyle = () => (
  <style>{`
    @keyframes cq-fade-up { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
    .cq-fade { animation: cq-fade-up 480ms cubic-bezier(0.22,1,0.36,1) both; }
    .cq-fade-2 { animation-delay: 70ms; } .cq-fade-3 { animation-delay: 140ms; } .cq-fade-4 { animation-delay: 210ms; }
    .cq-aurora { position: relative; }
    .cq-aurora::before { content:''; position: fixed; inset: 0; pointer-events: none; z-index: 0;
      background: radial-gradient(900px 600px at -10% -10%, rgba(235,37,141,0.08), transparent 60%),
                  radial-gradient(900px 600px at 110% 10%, rgba(52,209,176,0.08), transparent 60%); }
    .cq-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
    .cq-scroll::-webkit-scrollbar-thumb { background: rgba(220,240,245,0.14); border-radius: 99px; }
    .cq-row-hover { transition: border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease; }
    .cq-row-hover:hover { border-color: ${C.hairBright} !important; box-shadow: 0 10px 34px rgba(0,0,0,0.35); transform: translateY(-1px); }
    .cq-input-focus:focus { border-color: rgba(79,143,208,0.6) !important; }
    .cq-btn-lift:hover { transform: translateY(-1px); box-shadow: 0 12px 32px rgba(235,37,141,0.35); }
    @keyframes cq-dot-pulse { 0%,100% { box-shadow: 0 0 0 0 currentColor; } 50% { box-shadow: 0 0 0 5px transparent; } }
    .cq-dot-live { animation: cq-dot-pulse 2.4s ease-in-out infinite; }
  `}</style>
);

/** XeroHealthChip — floating status pill in the top-right of the CQ page.
 * Polls /api/admin/xero/status every 60s. Renders GREEN (healthy) / AMBER
 * (idle 45-60d) / RED (not configured/connected). Click → opens the OAuth
 * consent flow via /api/admin/xero/start (with `return_to=/admin/quotes`),
 * so tokens come back stored and the user lands right back where they were.
 */
const XeroHealthChip = ({ say }) => {
  const [status, setStatus] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const load = React.useCallback(async () => {
    try {
      const d = await req('GET', '/api/admin/xero/status');
      setStatus(d);
    } catch (e) {
      setStatus({ configured: false, connected: false, health: 'red', reason: e?.message || 'status_check_failed' });
    }
  }, []);
  React.useEffect(() => {
    load();
    // Poll on interval AND when user comes back to the tab (post-OAuth)
    const iv = setInterval(load, 60000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    // If Xero callback redirected here with ?xero=connected, refresh & toast.
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get('xero') === 'connected') {
        say && say('✓ Xero reconnected');
        setTimeout(load, 300);
        // Clean the URL without navigating
        const clean = window.location.pathname;
        window.history.replaceState({}, '', clean);
      }
    } catch (_) { /* SSR-safe */ }
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); };
  }, [load, say]);
  const reconnect = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const d = await req('GET', '/api/admin/xero/start?return_to=%2Fadmin%2Fquotes');
      if (d?.authorization_url) {
        window.location.href = d.authorization_url;
      } else {
        say && say('Reconnect failed: no authorization URL');
        setBusy(false);
      }
    } catch (e) {
      say && say(`Xero reconnect failed: ${e?.message || 'check /api/admin/xero/status'}`);
      setBusy(false);
    }
  };
  if (!status) {
    return null; // silent while loading
  }
  const health = status.health || (status.connected ? 'green' : 'red');
  const dotColor = health === 'green' ? '#34D1B0' : (health === 'amber' ? '#F0C36D' : '#EB258D');
  const bg = health === 'green' ? 'rgba(52,209,176,0.08)' : (health === 'amber' ? 'rgba(240,195,109,0.10)' : 'rgba(235,37,141,0.10)');
  const brd = health === 'green' ? 'rgba(52,209,176,0.35)' : (health === 'amber' ? 'rgba(240,195,109,0.45)' : 'rgba(235,37,141,0.45)');
  const label = health === 'green' ? `Xero · ${status.tenant_name || 'connected'}`
    : health === 'amber' ? `Xero · ${status.tenant_name || 'connected'} · freshen soon`
      : status.configured ? 'Xero · disconnected'
        : 'Xero · not configured';
  const title = status.reason || (health === 'green' ? `Connected to ${status.tenant_name || 'Xero'} · last refresh ${status.days_since_refresh ?? '?'}d ago` : '');
  return (
    <div data-testid="xero-health-chip"
      style={{ position: 'absolute', top: -6, right: 0, zIndex: 5,
               display: 'inline-flex', alignItems: 'center', gap: 10,
               background: bg, border: `1px solid ${brd}`, borderRadius: 999,
               padding: '6px 12px 6px 10px', fontFamily: 'Inter', fontSize: 11.5,
               fontWeight: 700, letterSpacing: '0.02em', color: '#E6F6F4',
               backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      title={title}>
      <span data-testid={`xero-health-dot-${health}`}
        className="cq-dot-live"
        style={{ width: 9, height: 9, borderRadius: 999, background: dotColor, color: dotColor,
                 boxShadow: `0 0 8px ${dotColor}` }} />
      <span>{label}</span>
      {(health !== 'green') && (
        <button data-testid="xero-reconnect-btn" onClick={reconnect} disabled={busy}
          style={{ background: 'transparent', border: `1px solid ${brd}`,
                   color: dotColor, borderRadius: 999, padding: '3px 10px',
                   marginLeft: 4, cursor: busy ? 'wait' : 'pointer',
                   fontFamily: 'Inter', fontSize: 10.5, fontWeight: 700,
                   letterSpacing: '0.02em' }}>
          {busy ? 'Opening…' : 'Reconnect Xero'}
        </button>
      )}
    </div>
  );
};

export default function V2ConsultingQuotesPage() {
  const [quotes, setQuotes] = useState(null);
  const [view, setView] = useState('list');
  const [sel, setSel] = useState(null);
  const [toast, setToast] = useState(null);
  const demo = typeof window !== 'undefined' && !!localStorage.getItem('desk-demo');
  const say = (m) => { setToast(m); setTimeout(() => setToast(null), 3500); };
  const [showArchived, setShowArchived] = useState(false);
  const load = useCallback(() => {
    const url = showArchived ? '/api/admin/cq/archived_quotes' : '/api/admin/cq/quotes';
    return req('GET', url).then((d) => setQuotes(d.quotes)).catch(() => setQuotes([]));
  }, [showArchived]);
  useEffect(() => { if (!demo) load(); }, [load, demo]);
  const openDetail = async (qid) => { setSel(await req('GET', `/api/admin/cq/quotes/${qid}`)); setView('detail'); };
  const [editing, setEditing] = useState(null);
  const openEdit = async (qid) => {
    const q = await req('GET', `/api/admin/cq/quotes/${qid}`);
    if (q.status === 'accepted' || q.status === 'accepted_pending') {
      say('Undo acceptance first — accepted quotes are locked'); return;
    }
    setEditing(q); setView('compose');
  };

  // List-row inline actions — MJ can act without opening the detail view.
  const rowAction = async (fn, ok, err = 'Something went wrong') => {
    try { await fn(); say(ok); load(); }
    catch (e) { say(err + ': ' + (e?.message || 'unknown')); }
  };
  const rowDuplicate     = (qid)    => rowAction(() => req('POST', `/api/admin/cq/quotes/${qid}/duplicate`), '📄 Duplicated');
  const rowArchive       = (qid)    => rowAction(() => req('POST', `/api/admin/cq/quotes/${qid}/archive`),   '🗄 Archived · view via Archive drawer');
  const rowRestore       = (qid)    => rowAction(() => req('POST', `/api/admin/cq/quotes/${qid}/restore`),   '↺ Restored');
  const rowDestroy       = async (qid, num) => {
    if (!window.confirm(`Permanently delete quote ${num}?\nThis cannot be undone.`)) return;
    return rowAction(() => req('DELETE', `/api/admin/cq/quotes/${qid}?confirm=DELETE`), '🗑 Deleted');
  };
  const rowUndoAcceptance = async (qid, num) => {
    const reason = window.prompt(`Undo acceptance on quote ${num}?\n(Optional reason for the audit log)`, '');
    if (reason === null) return;
    return rowAction(async () => {
      const r = await req('POST', `/api/admin/cq/quotes/${qid}/revert_acceptance`, { confirm: 'REVERT', reason });
      if (r?.warnings?.length) say('⚠ Reverted — ' + r.warnings.join(' · '));
    }, '↺ Acceptance reverted · job cancelled' );
  };
  const rowXeroRetry = async (qid, num) => {
    try {
      const r = await req('POST', `/api/admin/cq/quotes/${qid}/xero_retry`);
      say(`✓ Xero draft ${r.xero_invoice_number || 'created'} for ${num}`);
      load();
    } catch (e) {
      const msg = String(e?.message || '');
      const authIssue = /401|unauthori|token|refresh|reconnect|not_connected|XeroNotConnected|invalid_grant/i.test(msg);
      say(authIssue
        ? `Xero auth failed for ${num} — click "Reconnect Xero" in the header to relink`
        : `Xero retry failed for ${num}: ${msg || 'check /admin/xero/status'}`);
    }
  };
  const rowXeroRepair = async (qid, num) => {
    if (!window.confirm(
      `Repair Xero draft for ${num}?\n\n` +
      `This updates the line items on the existing DRAFT invoice (preserving the invoice number) ` +
      `using the current line items in the OS. Fixes the qty × unit-price display.\n\n` +
      `Only works if the invoice is still DRAFT in Xero.`
    )) return;
    try {
      const r = await req('POST', `/api/admin/cq/quotes/${qid}/xero_retry?repair=true`);
      say(`✓ Xero ${r.xero_invoice_number || 'draft'} repaired — ${r.lines_pushed || '?'} lines pushed`);
      load();
    } catch (e) {
      const msg = String(e?.message || '');
      const authIssue = /401|unauthori|token|refresh|reconnect|not_connected|XeroNotConnected|invalid_grant/i.test(msg);
      say(authIssue
        ? `Xero auth failed for ${num} — click "Reconnect Xero" in the header to relink`
        : `Xero repair failed for ${num}: ${msg || 'invoice may no longer be DRAFT'}`);
    }
  };

  if (demo) {
    return (
      <Chrome activeTab="quotes" crumb="Consulting quotes">
        <div data-testid="cq-demo-mask" style={{ ...card, textAlign: 'center', padding: 60 }}>
          <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 14, color: C.faint }}>🎭 Quotes are hidden while presenting — turn off Demo mode on the Desk.</p>
        </div>
      </Chrome>
    );
  }

  return (
    <Chrome activeTab="quotes" crumb="Consulting quotes — compose, send, track, win">
      <CQStyle />
      <div className="cq-aurora" style={{ position: 'relative', zIndex: 1 }}>
        <XeroHealthChip say={say} />
        {toast && <div data-testid="cq-toast" style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 3000, background: C.surface, border: `1px solid ${C.hairBright}`, color: C.icy, padding: '11px 24px', borderRadius: 999, fontFamily: 'Inter', fontSize: 13, fontWeight: 600, boxShadow: '0 14px 40px rgba(0,0,0,0.5)' }}>{toast}</div>}
        {view === 'list' && <QuoteList quotes={quotes} showArchived={showArchived} setShowArchived={setShowArchived}
          onNew={() => { setEditing(null); setView('compose'); }} onOpen={openDetail} onEdit={openEdit}
          rowActions={{ rowDuplicate, rowArchive, rowRestore, rowDestroy, rowUndoAcceptance }}
          xeroRetry={rowXeroRetry} xeroRepair={rowXeroRepair} />}
        {view === 'compose' && <Composer say={say} editQuote={editing} onDone={(q) => { setEditing(null); load(); openDetail(q.quote_id); }} onBack={() => { setEditing(null); setView('list'); }} />}
        {view === 'detail' && sel && <Detail q={sel} say={say} reload={() => openDetail(sel.quote_id)} onBack={() => { load(); setView('list'); }} onEdit={() => openEdit(sel.quote_id)} />}
      </div>
    </Chrome>
  );
}

const QuoteList = ({ quotes, onNew, onOpen, onEdit, showArchived, setShowArchived, rowActions, xeroRetry, xeroRepair }) => {
  const [menu, setMenu] = useState(null); // { qid, anchorRect }
  const [kind, setKind] = useState(() => localStorage.getItem('cq-list-kind') || 'quotes'); // 'quotes' | 'invoices' | 'all'
  useEffect(() => { localStorage.setItem('cq-list-kind', kind); }, [kind]);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menu) return;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(null); };
    const onScroll = () => setMenu(null);
    document.addEventListener('mousedown', close);
    window.addEventListener('resize', onScroll);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [menu]);
  const build = (typeof window !== 'undefined' && window.__KINDRA_BUILD__) || 'dev';
  const openMenu = (e, qid) => {
    e.stopPropagation();
    if (menu && menu.qid === qid) { setMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenu({ qid, anchorRect: { top: rect.bottom + 6, right: window.innerWidth - rect.right } });
  };
  const activeQuote = menu ? (quotes || []).find((q) => q.quote_id === menu.qid) : null;
  const activeAccepted = activeQuote && (activeQuote.status === 'accepted' || activeQuote.status === 'accepted_pending');
  // Split quotes vs invoices per kind tab
  const allQuotes = quotes || [];
  const quoteCount = allQuotes.filter((q) => q.mode !== 'invoice').length;
  const invoiceCount = allQuotes.filter((q) => q.mode === 'invoice').length;
  const filtered = kind === 'all' ? allQuotes
    : kind === 'invoices' ? allQuotes.filter((q) => q.mode === 'invoice')
    : allQuotes.filter((q) => q.mode !== 'invoice');
  const tabBtn = (val, label, count) => (
    <button data-testid={`cq-tab-${val}`} onClick={() => setKind(val)}
      style={{
        padding: '7px 14px', borderRadius: 999, cursor: 'pointer',
        background: kind === val ? 'rgba(52,209,176,0.14)' : 'transparent',
        border: `1px solid ${kind === val ? '#34D1B0' : C.hair}`,
        color: kind === val ? '#34D1B0' : C.muted,
        fontFamily: 'Inter', fontSize: 12.5, fontWeight: 700,
        letterSpacing: '0.02em', transition: 'all 180ms ease',
      }}>{label} <span style={{ opacity: 0.65, fontWeight: 500, marginLeft: 4 }}>({count})</span></button>
  );
  return (
  <div data-testid="cq-list">
    <div className="cq-fade" style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <span style={eyebrow}>Consulting</span>
        <h1 style={{ ...hFont, margin: 0, fontSize: 30, letterSpacing: '-0.01em' }}>{kind === 'invoices' ? 'Invoices' : (kind === 'all' ? 'All' : 'Quotes')}{showArchived ? ' — Archive' : ''}</h1>
      </div>
      <button data-testid="cq-toggle-archive" onClick={() => setShowArchived(!showArchived)}
        style={{ ...ghost, cursor: 'pointer' }}>{showArchived ? '← Active' : '🗄 Archive'}</button>
      {!showArchived && <button data-testid="cq-new-btn" className="cq-btn-lift" onClick={onNew} style={btnPrimary}>+ New quote</button>}
    </div>
    {/* Kind tabs — Quotes vs Invoices (matches Money-tile split) */}
    <div className="cq-fade" style={{ display: 'flex', gap: 8, marginBottom: 22, flexWrap: 'wrap' }}>
      {tabBtn('quotes', 'Quotes', quoteCount)}
      {tabBtn('invoices', 'Invoices', invoiceCount)}
      {tabBtn('all', 'All', allQuotes.length)}
    </div>
    {quotes === null && <p style={{ color: C.faint, fontFamily: 'Inter' }}>…</p>}
    {quotes && filtered.length === 0 && (
      <div className="cq-fade cq-fade-2" style={{ ...card, textAlign: 'center', padding: 48 }}>
        <p style={{ color: C.muted, fontFamily: 'Inter', margin: 0, fontSize: 14 }}>
          {showArchived
            ? 'Archive is empty — nothing archived yet.'
            : kind === 'invoices'
              ? 'No invoices yet — use "Invoice mode" on a new quote to push a Xero draft directly.'
              : kind === 'quotes'
                ? 'No quotes yet — hit "+ New quote" and dump your rough notes. The studio writes the line items.'
                : 'Nothing yet.'}
        </p>
      </div>
    )}
    <div style={{ display: 'grid', gap: 10 }}>
      {filtered.map((q, i) => {
        const isAccepted = q.status === 'accepted' || q.status === 'accepted_pending';
        return (
        <div key={q.quote_id} data-testid={`cq-row-${q.quote_id}`} className="cq-fade cq-row-hover" onClick={() => onOpen(q.quote_id)}
          style={{ ...card, animationDelay: `${Math.min(i, 6) * 60}ms`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', borderLeft: `3px solid ${STATUS_C[q.status] || C.hair}` }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <p style={{ ...hFont, margin: 0, fontSize: 16.5 }}>
              {q.mode === 'invoice' && <span data-testid={`cq-row-mode-invoice-${q.quote_id}`} style={{ display: 'inline-block', padding: '2px 7px', marginRight: 8, borderRadius: 6, background: 'rgba(240,195,109,0.14)', border: '1px solid rgba(240,195,109,0.35)', color: '#F0C36D', fontFamily: 'Inter', fontWeight: 800, fontSize: 9.5, letterSpacing: '0.08em', verticalAlign: 'middle' }}>🧾 INVOICE</span>}
              {q.business_name} <span style={{ color: C.faint, fontFamily: 'Inter', fontWeight: 500, fontSize: 12 }}>· {q.quote_number}</span>
            </p>
            <p style={{ margin: '5px 0 0', fontFamily: 'Inter', fontSize: 12, color: C.muted }}>
              <b style={{ color: C.icy }}>{money(q.total_inc)}</b> inc GST · {Math.round(q.days_out)}d · {q.view_count} view{q.view_count === 1 ? '' : 's'}
              {q.vision_engagement > 0 && <span style={{ color: '#34D1B0', fontWeight: 700 }}> · 👀 explored the vision ×{q.vision_engagement}</span>}
              {q.flagged_for_call && <span style={{ color: '#EB258D', fontWeight: 700 }}> · 📞 call time</span>}
            </p>
          </div>
          <span style={chip(STATUS_C[q.status] || C.faint)}>{q.status}</span>
          {/* Xero-draft retry — visible only on invoice-mode rows that
              had a Xero push failure (xero_error set, no invoice_id). */}
          {q.mode === 'invoice' && q.xero_error && !q.xero_invoice_id && (
            <button data-testid={`cq-row-xero-retry-${q.quote_id}`}
              onClick={(e) => { e.stopPropagation(); xeroRetry(q.quote_id, q.quote_number); }}
              title={`Xero error: ${q.xero_error || 'unknown'}`}
              style={{ background: 'rgba(240,195,109,0.14)', border: '1px solid rgba(240,195,109,0.45)', color: '#F0C36D',
                       padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
                       fontFamily: 'Inter', fontSize: 11, fontWeight: 700, letterSpacing: '0.02em' }}>
              ↻ Retry Xero
            </button>
          )}
          {q.mode === 'invoice' && q.xero_invoice_number && (
            <>
              <span data-testid={`cq-row-xero-ok-${q.quote_id}`}
                style={{ background: 'rgba(52,209,176,0.10)', border: '1px solid rgba(52,209,176,0.4)', color: '#34D1B0',
                         padding: '5px 10px', borderRadius: 999,
                         fontFamily: 'Inter', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.02em' }}>
                ✓ Xero {q.xero_invoice_number}
              </span>
              <button data-testid={`cq-row-xero-repair-${q.quote_id}`}
                onClick={(e) => { e.stopPropagation(); xeroRepair(q.quote_id, q.quote_number); }}
                title="Update the Xero draft's line items in place (fixes qty × unit-price display)"
                style={{ background: 'transparent', border: `1px solid ${C.hair}`, color: C.faint,
                         padding: '4px 10px', borderRadius: 999, cursor: 'pointer',
                         fontFamily: 'Inter', fontSize: 10, fontWeight: 600, letterSpacing: '0.03em' }}>
                ↻ Repair lines
              </button>
            </>
          )}
          {isAccepted && (
            <button data-testid={`cq-row-undo-${q.quote_id}`}
              onClick={(e) => { e.stopPropagation(); rowActions.rowUndoAcceptance(q.quote_id, q.quote_number); }}
              style={{ background: 'transparent', border: '1px solid rgba(255,211,125,0.4)', color: '#FFD37D',
                       padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
                       fontFamily: 'Inter', fontSize: 11, fontWeight: 700, letterSpacing: '0.02em' }}>
              ↺ Undo acceptance
            </button>
          )}
          <button data-testid={`cq-row-menu-${q.quote_id}`}
            onClick={(e) => openMenu(e, q.quote_id)}
            aria-label="Row actions"
            style={{ background: 'transparent', border: `1px solid ${C.hair}`, color: C.icy,
                     width: 32, height: 32, borderRadius: 999, cursor: 'pointer',
                     fontFamily: 'Inter', fontSize: 16, lineHeight: 1, display: 'grid', placeItems: 'center' }}>⋯</button>
        </div>
        );
      })}
    </div>
    {menu && activeQuote && createPortal(
      <div ref={menuRef} data-testid={`cq-menu-portal-${menu.qid}`} onClick={(e) => e.stopPropagation()}
        style={{ position: 'fixed', top: menu.anchorRect.top, right: menu.anchorRect.right, zIndex: 5000,
                 background: C.surface, border: `1px solid ${C.hairBright}`, borderRadius: 12,
                 padding: 6, boxShadow: '0 20px 60px rgba(0,0,0,0.6)', minWidth: 220 }}>
        {activeAccepted && <MenuBtn testid={`cq-menu-undo-${menu.qid}`} onClick={() => { setMenu(null); rowActions.rowUndoAcceptance(menu.qid, activeQuote.quote_number); }} tone="#FFD37D">↺ Undo acceptance</MenuBtn>}
        {!activeAccepted && !activeQuote.archived && <MenuBtn testid={`cq-menu-edit-${menu.qid}`} onClick={() => { setMenu(null); onEdit(menu.qid); }} tone="#34D1B0">✏ Edit</MenuBtn>}
        <MenuBtn testid={`cq-menu-duplicate-${menu.qid}`} onClick={() => { setMenu(null); rowActions.rowDuplicate(menu.qid); }}>📄 Duplicate</MenuBtn>
        {!activeQuote.archived && <MenuBtn testid={`cq-menu-archive-${menu.qid}`} onClick={() => { setMenu(null); rowActions.rowArchive(menu.qid); }}>🗄 Archive</MenuBtn>}
        {activeQuote.archived && <MenuBtn testid={`cq-menu-restore-${menu.qid}`} onClick={() => { setMenu(null); rowActions.rowRestore(menu.qid); }}>↺ Restore</MenuBtn>}
        {activeQuote.archived && <MenuBtn testid={`cq-menu-delete-${menu.qid}`} onClick={() => { setMenu(null); rowActions.rowDestroy(menu.qid, activeQuote.quote_number); }} tone="#FF8FC1">🗑 Delete forever</MenuBtn>}
      </div>,
      document.body,
    )}
    <div data-testid="cq-build-stamp" style={{ marginTop: 40, textAlign: 'center', fontFamily: 'Inter', fontSize: 10.5, color: 'rgba(220,240,245,0.32)', letterSpacing: '0.06em' }}>
      TravelTrucks · build {build}
    </div>
  </div>
  );
};

const MenuBtn = ({ children, onClick, testid, tone }) => (
  <button data-testid={testid} onClick={onClick}
    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px',
             background: 'transparent', border: 'none', color: tone || C.icy,
             fontFamily: 'Inter', fontSize: 13.5, fontWeight: 600, borderRadius: 8, cursor: 'pointer' }}
    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(220,240,245,0.06)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
    {children}
  </button>
);

const emptyItem = () => ({ id: Math.random().toString(36).slice(2, 10), title: '', description: '', qty: 1, unit_price: 0, gst: true, library_id: null });

const emptyExt = () => ({ id: Math.random().toString(36).slice(2, 10), name: '', purpose: '', est_cost_aud: 0, period: 'mo', billed_by: 'third_party', required: true, library_id: null });

const PERIOD_LABEL = { mo: '/mo', yr: '/yr', one_off: 'one-off' };
const BILLED_LABEL = { third_party: 'Third party directly', via_kindra: 'Via TravelTrucks' };

const ExtServiceBlock = ({ x, i, upd, remove }) => (
  <div data-testid={`cq-ext-${i}`} className="cq-fade" style={{ background: 'rgba(31,182,193,0.05)', border: `1px solid ${C.hair}`, borderLeft: '3px solid #1FB6C1', borderRadius: 14, padding: '14px 16px', marginBottom: 10 }}>
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <input data-testid={`cq-ext-name-${i}`} className="cq-input-focus" placeholder="Service name (e.g. Claude API)" value={x.name} onChange={(e) => upd(i, 'name', e.target.value)}
        style={{ flex: 2, minWidth: 180, background: 'transparent', border: 'none', color: C.icy, fontFamily: 'Poppins, Inter', fontWeight: 600, fontSize: 14.5, outline: 'none', padding: '2px 0' }} />
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span style={{ fontFamily: 'Inter', fontSize: 11, color: C.faint }}>~ $</span>
        <input data-testid={`cq-ext-cost-${i}`} className="cq-input-focus" type="number" min="0" step="0.01" value={x.est_cost_aud} onChange={(e) => upd(i, 'est_cost_aud', e.target.value)} style={{ ...inp, width: 90, padding: '6px 10px', textAlign: 'right', fontFamily: 'Poppins, Inter', fontWeight: 600 }} />
        <select data-testid={`cq-ext-period-${i}`} value={x.period} onChange={(e) => upd(i, 'period', e.target.value)} style={{ ...inp, width: 84, padding: '6px 8px', fontSize: 12 }}>
          <option value="mo">/mo</option>
          <option value="yr">/yr</option>
          <option value="one_off">one-off</option>
        </select>
      </div>
    </div>
    <textarea data-testid={`cq-ext-purpose-${i}`} className="cq-input-focus" rows={2} placeholder="Plain-English purpose (e.g. 'Powers the AI assistant')" value={x.purpose} onChange={(e) => upd(i, 'purpose', e.target.value)}
      style={{ ...inp, marginTop: 8, resize: 'vertical', fontSize: 12, lineHeight: 1.6, color: C.muted }} />
    <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'Inter', fontSize: 11, color: C.muted, cursor: 'pointer' }}>
        <span style={{ color: C.faint }}>Billed by</span>
        <select data-testid={`cq-ext-billed-${i}`} value={x.billed_by} onChange={(e) => upd(i, 'billed_by', e.target.value)} style={{ ...inp, width: 'auto', padding: '4px 8px', fontSize: 11 }}>
          <option value="third_party">Third party directly</option>
          <option value="via_kindra">Via TravelTrucks</option>
        </select>
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'Inter', fontSize: 11, color: C.muted, cursor: 'pointer' }}>
        <input data-testid={`cq-ext-required-${i}`} type="checkbox" checked={!!x.required} onChange={(e) => upd(i, 'required', e.target.checked)} style={{ accentColor: '#34D1B0' }} />
        Required (unchecked = optional)
      </label>
      <div style={{ flex: 1 }} />
      <button data-testid={`cq-ext-del-${i}`} onClick={() => remove(i)} style={{ ...ghost, fontSize: 10.5, padding: '4px 12px', color: '#FF8FC1', borderColor: 'rgba(235,37,141,0.35)' }}>remove</button>
    </div>
  </div>
);

const LineItemBlock = ({ li, i, upd, remove, saveToLib }) => {
  const acc = ACCENTS[i % ACCENTS.length];
  return (
    <div data-testid={`cq-item-${i}`} className="cq-fade" style={{ background: 'rgba(220,240,245,0.04)', border: `1px solid ${C.hair}`, borderLeft: `3px solid ${acc}`, borderRadius: 14, padding: '16px 18px', marginBottom: 12 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <input data-testid={`cq-item-title-${i}`} className="cq-input-focus" placeholder="Line item title" value={li.title} onChange={(e) => upd(i, 'title', e.target.value)}
          style={{ flex: 2, minWidth: 200, background: 'transparent', border: 'none', color: C.icy, fontFamily: 'Poppins, Inter', fontWeight: 600, fontSize: 16.5, outline: 'none', padding: '2px 0' }} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ ...eyebrow, margin: 0, fontSize: 9 }}>Qty</label>
            <input data-testid={`cq-item-qty-${i}`} className="cq-input-focus" type="number" min="0" step="1" value={li.qty} onChange={(e) => upd(i, 'qty', e.target.value)} style={{ ...inp, width: 64, textAlign: 'center', padding: '7px 8px' }} />
          </div>
          <div>
            <label style={{ ...eyebrow, margin: 0, fontSize: 9 }}>Unit (ex GST)</label>
            <input data-testid={`cq-item-price-${i}`} className="cq-input-focus" type="number" min="0" value={li.unit_price} onChange={(e) => upd(i, 'unit_price', e.target.value)} style={{ ...inp, width: 104, textAlign: 'right', padding: '7px 10px', fontFamily: 'Poppins, Inter', fontWeight: 600 }} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'Inter', fontSize: 11, color: C.muted, cursor: 'pointer', marginTop: 14 }}>
            <input data-testid={`cq-item-gst-${i}`} type="checkbox" checked={!!li.gst} onChange={(e) => upd(i, 'gst', e.target.checked)} style={{ accentColor: '#34D1B0' }} />GST
          </label>
          <b style={{ fontFamily: 'Poppins, Inter', fontWeight: 700, fontSize: 17, color: C.icy, marginTop: 12, minWidth: 84, textAlign: 'right' }}>{money((li.qty || 1) * (li.unit_price || 0) * (li.gst ? 1.1 : 1))}</b>
        </div>
      </div>
      <textarea data-testid={`cq-item-desc-${i}`} className="cq-input-focus" rows={2} placeholder="Client-facing description — what they get, why it matters" value={li.description} onChange={(e) => upd(i, 'description', e.target.value)}
        style={{ ...inp, marginTop: 10, resize: 'vertical', fontSize: 12.5, lineHeight: 1.6, color: C.muted }} />
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        {!li.library_id && li.title && <button data-testid={`cq-item-savelib-${i}`} onClick={() => saveToLib(li)} style={{ ...ghost, fontSize: 10.5, padding: '4px 12px' }}>☆ save to library</button>}
        <button data-testid={`cq-item-del-${i}`} onClick={() => remove(i)} style={{ ...ghost, fontSize: 10.5, padding: '4px 12px', color: '#FF8FC1', borderColor: 'rgba(235,37,141,0.35)' }}>remove</button>
      </div>
    </div>
  );
};

const Composer = ({ say, onDone, onBack, editQuote = null }) => {
  const isEdit = !!editQuote;
  const [custQ, setCustQ] = useState('');
  const [custs, setCusts] = useState([]);
  const [lead, setLead] = useState(null);
  const [nc, setNc] = useState(null);
  const [rough, setRough] = useState('');
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [intro, setIntro] = useState('');
  const [validity, setValidity] = useState(14);
  const [lib, setLib] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  // Mode: "quote" (default — hosted page + send + accept + Xero-on-accept)
  // or "invoice" (skip page/send/accept — Xero DRAFT immediately). Locked
  // on edit — you don't switch mid-record; duplicate to convert.
  const [mode, setMode] = useState((editQuote && editQuote.mode) || 'quote');
  const [createJobOnInvoice, setCreateJobOnInvoice] = useState(true);
  // Commercial options — delivery guarantee (weeks + label), optional
  // priority-upgrade tier (weeks + $ or %), optional pay-in-full discount.
  // Both toggles OFF by default; MJ opts in per quote.
  const [co, setCo] = useState({
    delivery_weeks: (editQuote && editQuote.commercial_options && editQuote.commercial_options.delivery_weeks) || '',
    guarantee_label: (editQuote && editQuote.commercial_options && editQuote.commercial_options.guarantee_label) || 'guaranteed',
    priority_enabled: !!(editQuote && editQuote.commercial_options && editQuote.commercial_options.priority_enabled),
    priority_weeks: (editQuote && editQuote.commercial_options && editQuote.commercial_options.priority_weeks) || '',
    priority_mode: ((editQuote && editQuote.commercial_options && editQuote.commercial_options.priority_percent != null)
      ? 'percent' : '$'),
    priority_price_aud: (editQuote && editQuote.commercial_options && editQuote.commercial_options.priority_price_aud) || '',
    priority_percent: (editQuote && editQuote.commercial_options && editQuote.commercial_options.priority_percent) || '',
    priority_scarcity_enabled: !!(editQuote && editQuote.commercial_options && editQuote.commercial_options.priority_scarcity_enabled),
    priority_scarcity_text: (editQuote && editQuote.commercial_options && editQuote.commercial_options.priority_scarcity_text) || 'Limited priority slots available',
    payinfull_enabled: !!(editQuote && editQuote.commercial_options && editQuote.commercial_options.payinfull_enabled),
    payinfull_discount_percent: (editQuote && editQuote.commercial_options && editQuote.commercial_options.payinfull_discount_percent) || '',
  });
  const setCoK = (k, v) => setCo((s) => ({ ...s, [k]: v }));
  // Pack commercial options for the API — strip the FE-only 'priority_mode'
  // toggle and translate to whichever field the user set ($ vs %).
  const packCommercialOptions = () => ({
    delivery_weeks: co.delivery_weeks === '' ? null : Number(co.delivery_weeks),
    guarantee_label: co.guarantee_label || 'guaranteed',
    priority_enabled: !!co.priority_enabled,
    priority_weeks: co.priority_enabled && co.priority_weeks !== '' ? Number(co.priority_weeks) : null,
    priority_price_aud: co.priority_enabled && co.priority_mode === '$' && co.priority_price_aud !== '' ? Number(co.priority_price_aud) : null,
    priority_percent: co.priority_enabled && co.priority_mode === 'percent' && co.priority_percent !== '' ? Number(co.priority_percent) : null,
    priority_scarcity_enabled: !!(co.priority_enabled && co.priority_scarcity_enabled),
    priority_scarcity_text: co.priority_scarcity_text,
    payinfull_enabled: !!co.payinfull_enabled,
    payinfull_discount_percent: co.payinfull_enabled && co.payinfull_discount_percent !== '' ? Number(co.payinfull_discount_percent) : null,
  });
  // External services (running costs)
  const [ext, setExt] = useState([]);
  const [extLib, setExtLib] = useState([]);
  const [extSuggest, setExtSuggest] = useState([]);
  const [extBusy, setExtBusy] = useState(false);
  useEffect(() => { req('GET', '/api/admin/cq/library').then((d) => setLib(d.items)).catch(() => {}); }, []);
  useEffect(() => { req('GET', '/api/admin/cq/services_library').then((d) => setExtLib(d.items || [])).catch(() => {}); }, []);
  // Hydrate composer from an existing quote when editing. Fetch the lead
  // separately since the quote payload only carries lead_id.
  useEffect(() => {
    if (!editQuote) return;
    setItems((editQuote.line_items || []).map((li) => ({ ...li, id: li.id || Math.random().toString(36).slice(2, 10) })));
    setExt((editQuote.external_services || []).map((x) => ({ ...x, id: x.id || Math.random().toString(36).slice(2, 10) })));
    setIntro(editQuote.intro || '');
    setValidity(editQuote.validity_days || 14);
    if (editQuote.lead_id) {
      req('GET', `/api/admin/cq/customers?q=${encodeURIComponent(editQuote.business_name || '')}`)
        .then((d) => {
          const match = (d.customers || []).find((c) => c.lead_id === editQuote.lead_id);
          if (match) setLead(match);
          else setLead({ lead_id: editQuote.lead_id, first_name: editQuote.contact_first_name || '',
                          business_name: editQuote.business_name || '', email: editQuote.contact_email || '' });
        })
        .catch(() => setLead({ lead_id: editQuote.lead_id, first_name: editQuote.contact_first_name || '',
                                business_name: editQuote.business_name || '', email: editQuote.contact_email || '' }));
    }
  }, [editQuote]);
  useEffect(() => {
    const t = setTimeout(() => req('GET', `/api/admin/cq/customers?q=${encodeURIComponent(custQ)}`).then((d) => setCusts(d.customers)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [custQ]);

  const totals = items.reduce((a, li) => {
    const ex = (Number(li.qty) || 1) * (Number(li.unit_price) || 0);
    a.ex += ex; if (li.gst) a.gst += ex * 0.1; return a;
  }, { ex: 0, gst: 0 });

  const askAI = async () => {
    if (!rough.trim()) return say('Type your request first');
    setBusy(true);
    try {
      const d = await req('POST', '/api/admin/cq/ai_draft', {
        rough_text: rough, existing_items: items,
        customer: lead ? `${lead.first_name || ''} — ${lead.business_name || ''}` : (nc?.business || nc?.name || ''),
      });
      setUndoStack((s) => [...s.slice(-9), { items: JSON.parse(JSON.stringify(items)), label: d.summary || 'AI change' }]);
      let next = items.filter((li) => !(d.remove || []).includes(li.id));
      next = next.map((li) => { const u = (d.update || []).find((x) => x.id === li.id); return u ? { ...li, ...u.fields } : li; });
      next = [...next, ...(d.add || [])];
      setItems(next);
      if (d.intro_suggestion && !intro) setIntro(d.intro_suggestion);
      setRough('');
      say(`✨ ${d.summary || 'Done'} — Undo available`);
    } catch (e) { say('AI draft failed — try again'); }
    setBusy(false);
  };
  const undo = () => {
    const last = undoStack[undoStack.length - 1];
    if (!last) return;
    setItems(last.items);
    setUndoStack(undoStack.slice(0, -1));
    say(`↺ Undid: ${last.label}`);
  };
  // ALL mutation helpers use functional updaters so rapid successive changes
  // (library-click + AI-confirm + manual-add fired in the same render tick)
  // never lose state to a stale closure. Same bug class as the original
  // line-items overwrite. Fixed for both line items AND external services.
  const upd = (i, k, v) => setItems((prev) => prev.map((li, k2) => (k2 === i ? { ...li, [k]: v } : li)));
  const saveToLib = async (li) => {
    try { await req('POST', '/api/admin/cq/library', { title: li.title, description: li.description, unit_price: li.unit_price, gst: li.gst }); say('✓ Saved to library'); req('GET', '/api/admin/cq/library').then((d) => setLib(d.items)); }
    catch { say('Could not save'); }
  };
  const save = async () => {
    if (!lead && !(nc && nc.name)) return say('Pick or create a customer first');
    if (!items.length) return say('Add at least one line item');
    setBusy(true);
    try {
      if (isEdit) {
        // Edit — patch the existing quote, preserving token/quote_number/URL
        await req('PATCH', `/api/admin/cq/quotes/${editQuote.quote_id}`, {
          intro, validity_days: Number(validity) || 14, line_items: items, external_services: ext,
          commercial_options: packCommercialOptions(),
        });
        say(`✓ ${editQuote.quote_number} updated`);
        onDone({ quote_id: editQuote.quote_id });
      } else {
        const d = await req('POST', '/api/admin/cq/quotes', {
          mode, create_job_on_invoice: mode === 'invoice' ? createJobOnInvoice : undefined,
          lead_id: lead?.lead_id, new_customer: nc, intro,
          validity_days: Number(validity) || 14, line_items: items, external_services: ext,
          commercial_options: packCommercialOptions(),
        });
        if (mode === 'invoice') {
          const q = d.quote;
          if (q.xero_error) {
            say(`⚠ ${q.quote_number} created — Xero draft failed: ${q.xero_error.slice(0, 80)}`);
          } else if (q.xero_invoice_number) {
            say(`🧾 ${q.quote_number} → Xero draft ${q.xero_invoice_number}${q.job_id ? ' · job created' : ''}`);
          } else {
            say(`🧾 ${q.quote_number} drafted${q.job_id ? ' · job created' : ''}`);
          }
        } else {
          say(`✓ ${d.quote.quote_number} saved as draft`);
        }
        onDone(d.quote);
      }
    } catch (e) { say('Save failed: ' + e.message); }
    setBusy(false);
  };

  // ── External services helpers ───────────────────────────────────
  const updExt = (i, k, v) => setExt((prev) => prev.map((x, k2) => (k2 === i ? { ...x, [k]: v } : x)));
  const removeExt = (k) => setExt((prev) => prev.filter((_, y) => y !== k));
  const addExtBlank = () => setExt((prev) => [...prev, emptyExt()]);
  const addExtFromLib = (l) => setExt((prev) => [...prev, {
    id: Math.random().toString(36).slice(2, 10), name: l.name, purpose: l.purpose || '',
    est_cost_aud: l.est_cost_aud || 0, period: l.period || 'mo',
    billed_by: l.billed_by || 'third_party', required: true, library_id: l.item_id,
  }]);
  const suggestExt = async () => {
    if (!items.length) return say('Add line items first, then I can suggest services');
    setExtBusy(true);
    try {
      const d = await req('POST', '/api/admin/cq/services_ai_suggest', {
        line_items: items,
        customer: lead ? `${lead.first_name || ''} — ${lead.business_name || ''}` : (nc?.business || nc?.name || ''),
      });
      // Filter out services already attached — read latest ext via setter
      setExt((prev) => {
        const already = new Set(prev.map((x) => x.library_id).filter(Boolean));
        setExtSuggest((d.suggestions || []).filter((s) => !already.has(s.library_id)));
        return prev; // no mutation, just reading latest
      });
      if (!d.suggestions?.length) say('No obvious external services from these items');
    } catch (e) { say('Suggest failed — try again'); }
    setExtBusy(false);
  };
  const confirmSuggestion = (s) => {
    setExt((prev) => [...prev, {
      id: Math.random().toString(36).slice(2, 10), name: s.name, purpose: s.purpose,
      est_cost_aud: s.est_cost_aud, period: s.period, billed_by: s.billed_by,
      required: true, library_id: s.library_id,
    }]);
    setExtSuggest((prev) => prev.filter((x) => x.library_id !== s.library_id));
  };

  return (
    <div data-testid="cq-composer" style={{ display: 'grid', gap: 16 }}>
      <div className="cq-fade" style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
        <button data-testid="cq-back" onClick={onBack} style={ghost}>← quotes</button>
        <div style={{ flex: 1 }}>
          <span style={eyebrow}>{mode === 'invoice' ? 'Compose invoice' : 'Compose'}</span>
          <h1 style={{ ...hFont, margin: 0, fontSize: 26, letterSpacing: '-0.01em' }}>{isEdit ? `Edit ${editQuote.quote_number}` : (mode === 'invoice' ? 'New invoice' : 'New quote')}</h1>
        </div>
        {/* Mode slider — locked while editing (dupe to convert). */}
        <div data-testid="cq-mode-toggle" style={{ display: 'inline-flex', padding: 4, borderRadius: 999, background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.hair}`, opacity: isEdit ? 0.5 : 1 }}>
          {[['quote', '📋 Quote'], ['invoice', '🧾 Invoice']].map(([k, label]) => {
            const active = mode === k;
            return (
              <button key={k} data-testid={`cq-mode-${k}`}
                disabled={isEdit}
                onClick={() => !isEdit && setMode(k)}
                title={k === 'invoice' ? 'Skip the client page — go straight to a Xero draft' : 'Full flow: hosted page, send, accept, then Xero'}
                style={{ padding: '7px 14px', borderRadius: 999, border: 'none',
                         background: active ? '#F0C36D' : 'transparent',
                         color: active ? '#0B0526' : C.icy,
                         fontFamily: 'Inter', fontSize: 12, fontWeight: 700,
                         cursor: isEdit ? 'default' : 'pointer', transition: 'all 0.15s' }}>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {mode === 'invoice' && !isEdit && (
        <div data-testid="cq-mode-invoice-notice" className="cq-fade" style={{ ...card, borderLeft: '3px solid #F0C36D', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 12.5, color: C.icy, flex: 1, minWidth: 220, lineHeight: 1.55 }}>
            🧾 <b>Invoice mode</b> — skips the hosted page + send flow. On Save: creates a Xero <b>DRAFT</b> (never auto-sent — you approve + send from Xero). External services excluded from the Xero invoice, kept on record for your reference.
          </p>
          <label data-testid="cq-invoice-createjob" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: 'Inter', fontSize: 12, color: C.icy, whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={createJobOnInvoice} onChange={(e) => setCreateJobOnInvoice(e.target.checked)}
              style={{ accentColor: '#F0C36D', width: 16, height: 16, cursor: 'pointer' }} />
            Create a Job for this invoice
          </label>
        </div>
      )}

      <section className="cq-fade cq-fade-2" style={card}>
        <span style={eyebrow}>Customer — single source of truth (Client Area)</span>
        {lead ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <p data-testid="cq-picked-customer" style={{ ...hFont, margin: 0, fontSize: 16, flex: 1, minWidth: 200 }}>
              <span style={{ backgroundImage: SPECTRUM, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{lead.first_name} {lead.business_name ? `— ${lead.business_name}` : ''}</span>
              <span style={{ color: C.faint, fontFamily: 'Inter', fontWeight: 400, fontSize: 12, marginLeft: 10 }}>{lead.email || ''}</span>
            </p>
            <button onClick={() => setLead(null)} style={ghost}>change</button>
          </div>
        ) : nc ? (
          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
            {['name', 'business', 'email', 'phone', 'address', 'abn'].map((k) => (
              <input key={k} data-testid={`cq-nc-${k}`} className="cq-input-focus" placeholder={k.toUpperCase()} value={nc[k] || ''} onChange={(e) => setNc({ ...nc, [k]: e.target.value })} style={inp} />
            ))}
            <button onClick={() => setNc(null)} style={ghost}>← search existing instead</button>
          </div>
        ) : (
          <>
            <input data-testid="cq-customer-search" className="cq-input-focus" placeholder="Search clients…" value={custQ} onChange={(e) => setCustQ(e.target.value)} style={inp} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              {custs.slice(0, 8).map((c) => (
                <button key={c.lead_id} data-testid={`cq-cust-${c.lead_id}`} onClick={() => setLead(c)} style={ghost}>
                  {c.first_name || '—'}{c.business_name ? ` · ${c.business_name}` : ''}
                </button>
              ))}
              <button data-testid="cq-quickcreate" onClick={() => setNc({})} style={{ ...ghost, color: '#34D1B0', borderColor: 'rgba(52,209,176,0.4)' }}>+ quick-create new</button>
            </div>
          </>
        )}
      </section>

      <section className="cq-fade cq-fade-3" style={{ ...card, borderColor: 'rgba(145,86,196,0.3)' }}>
        <span style={eyebrow}>Ask the studio — each request adds to the quote</span>
        <textarea data-testid="cq-rough" className="cq-input-focus" rows={2} placeholder='"add a booking agent build" · then "add 2 training sessions" · then "change item 2 to $900"' value={rough}
          onChange={(e) => setRough(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) askAI(); }}
          style={{ ...inp, resize: 'vertical', fontSize: 14, lineHeight: 1.6 }} />
        <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button data-testid="cq-ai-draft" className="cq-btn-lift" onClick={askAI} disabled={busy} style={btnPrimary}>{busy ? 'Writing…' : items.length ? '✨ Add with AI' : '✨ Draft with AI'}</button>
          {undoStack.length > 0 && (
            <button data-testid="cq-undo" onClick={undo} style={{ ...ghost, color: '#FFD37D', borderColor: 'rgba(255,211,125,0.4)' }}>↺ Undo — {undoStack[undoStack.length - 1].label}</button>
          )}
        </div>
        {lib.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <span style={{ ...eyebrow, marginBottom: 6 }}>Library — tap to add</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {lib.slice(0, 10).map((l) => (
                <button key={l.item_id} data-testid={`cq-lib-${l.item_id}`} onClick={() => setItems((prev) => [...prev, { ...emptyItem(), title: l.title, description: l.description, unit_price: l.unit_price, gst: l.gst !== false, library_id: l.item_id }])} style={{ ...ghost, fontSize: 11 }}>
                  {l.title} · ${l.unit_price}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="cq-fade cq-fade-4" style={card}>
        <span style={eyebrow}>Line items</span>
        {items.map((li, i) => (
          <LineItemBlock key={li.id} li={li} i={i} upd={upd} remove={(k) => setItems((prev) => prev.filter((_, x) => x !== k))} saveToLib={saveToLib} />
        ))}
        {items.length === 0 && <p style={{ fontFamily: 'Inter', fontSize: 13, color: C.faint, textAlign: 'center', padding: '18px 0' }}>Nothing yet — ask the studio above, tap a library item, or add one by hand.</p>}
        <button data-testid="cq-item-add" onClick={() => setItems((prev) => [...prev, emptyItem()])} style={{ ...ghost, marginTop: 4 }}>+ line item</button>
        <div data-testid="cq-totals" style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${C.hair}`, display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 18, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'Inter', fontSize: 12, color: C.faint }}>ex GST <b style={{ color: C.muted }}>{money(totals.ex)}</b></span>
          <span style={{ fontFamily: 'Inter', fontSize: 12, color: C.faint }}>GST <b style={{ color: C.muted }}>{money(totals.gst)}</b></span>
          <span style={{ fontFamily: 'Poppins, Inter', fontWeight: 700, fontSize: 26, backgroundImage: SPECTRUM, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{money(totals.ex + totals.gst)}</span>
        </div>
      </section>

      <section data-testid="cq-ext-section" className="cq-fade cq-fade-4" style={{ ...card, borderColor: 'rgba(31,182,193,0.3)', borderLeft: '3px solid #1FB6C1' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 4 }}>
          <span style={eyebrow}>External services — what they&rsquo;ll need to run it</span>
          <span style={{ fontFamily: 'Inter', fontSize: 10.5, color: C.faint, letterSpacing: '0.05em' }}>· separate from TravelTrucks&rsquo;s charges · excluded from Xero</span>
        </div>
        <p style={{ margin: '2px 0 14px', fontFamily: 'Inter', fontSize: 12.5, color: C.muted, lineHeight: 1.55 }}>
          Third-party subscriptions this build will need. Clients see these on the hosted page as running costs — clearly separated from the TravelTrucks total they accept.
        </p>
        {ext.map((x, i) => (
          <ExtServiceBlock key={x.id} x={x} i={i} upd={updExt} remove={removeExt} />
        ))}
        {ext.length === 0 && <p style={{ fontFamily: 'Inter', fontSize: 12.5, color: C.faint, textAlign: 'center', padding: '14px 0', margin: 0 }}>None yet — pick from the library, let the AI suggest, or add manually.</p>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
          <button data-testid="cq-ext-add" onClick={addExtBlank} style={ghost}>+ service</button>
          <button data-testid="cq-ext-suggest" onClick={suggestExt} disabled={extBusy || items.length === 0}
            style={{ ...ghost, color: '#1FB6C1', borderColor: 'rgba(31,182,193,0.4)', opacity: items.length === 0 ? 0.5 : 1 }}>
            {extBusy ? 'Thinking…' : '✨ Suggest from line items'}
          </button>
        </div>
        {extSuggest.length > 0 && (
          <div data-testid="cq-ext-suggestions" style={{ marginTop: 14, padding: '12px 14px', border: `1px dashed rgba(31,182,193,0.35)`, borderRadius: 12, background: 'rgba(31,182,193,0.04)' }}>
            <span style={{ ...eyebrow, marginBottom: 8, color: '#1FB6C1' }}>Suggestions — tap to confirm</span>
            {extSuggest.map((s) => (
              <div key={s.library_id} style={{ display: 'flex', gap: 10, padding: '8px 0', flexWrap: 'wrap', alignItems: 'center', borderTop: `1px solid ${C.hair}` }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <p style={{ margin: 0, fontFamily: 'Poppins, Inter', fontSize: 13, fontWeight: 600, color: C.icy }}>{s.name} <span style={{ color: C.faint, fontFamily: 'Inter', fontWeight: 400, fontSize: 11.5 }}>· ~${s.est_cost_aud}{PERIOD_LABEL[s.period]}</span></p>
                  <p style={{ margin: '3px 0 0', fontFamily: 'Inter', fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>{s.reason}</p>
                </div>
                <button data-testid={`cq-ext-confirm-${s.library_id}`} onClick={() => confirmSuggestion(s)}
                  style={{ ...ghost, color: '#34D1B0', borderColor: 'rgba(52,209,176,0.4)', fontSize: 11 }}>+ add</button>
              </div>
            ))}
          </div>
        )}
        {extLib.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <span style={{ ...eyebrow, marginBottom: 6 }}>Library — tap to add</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {extLib.slice(0, 12).map((l) => (
                <button key={l.item_id} data-testid={`cq-ext-lib-${l.item_id}`} onClick={() => addExtFromLib(l)} style={{ ...ghost, fontSize: 11 }}>
                  {l.name} · ~${l.est_cost_aud}{PERIOD_LABEL[l.period]}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="cq-fade cq-fade-4" style={{ ...card, display: 'grid', gap: 12, gridTemplateColumns: 'minmax(200px, 1fr) 130px' }}>
        <div>
          <span style={eyebrow}>Intro note (on the quote page)</span>
          <textarea data-testid="cq-intro" className="cq-input-focus" rows={2} value={intro} onChange={(e) => setIntro(e.target.value)} style={{ ...inp, resize: 'vertical' }} />
        </div>
        <div>
          <span style={eyebrow}>Valid (days)</span>
          <input data-testid="cq-validity" className="cq-input-focus" type="number" min="1" value={validity} onChange={(e) => setValidity(e.target.value)} style={inp} />
        </div>
      </section>

      {/* ── Commercial options ── delivery guarantee · priority upgrade ·
          pay-in-full discount. All OFF by default. Per-quote. Renders on
          the hosted quote page as the "Your options" area. */}
      {mode === 'quote' && (
      <section data-testid="cq-commercial-options" className="cq-fade cq-fade-4" style={{ ...card, display: 'grid', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={eyebrow}>Delivery & payment options</span>
          <span style={{ fontFamily: 'Inter', fontSize: 11, color: C.faint }}>· shown on the hosted quote page, total updates live for the client</span>
        </div>

        {/* Standard delivery guarantee — always shown, editable weeks */}
        <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 12, alignItems: 'end' }}>
          <div>
            <span style={{ ...eyebrow, fontSize: 10 }}>Delivered in (weeks)</span>
            <input data-testid="cq-delivery-weeks" className="cq-input-focus" type="number" min="0" value={co.delivery_weeks}
              onChange={(e) => setCoK('delivery_weeks', e.target.value)}
              placeholder="e.g. 4" style={inp} />
          </div>
          <div style={{ fontFamily: 'Inter', fontSize: 12.5, color: C.muted, lineHeight: 1.55, paddingBottom: 8 }}>
            Shown to the client as <b style={{ color: C.icy }}>{co.delivery_weeks ? `"Delivered in ${co.delivery_weeks} weeks — ${co.guarantee_label}"` : `"Delivered in N weeks — ${co.guarantee_label}"`}</b> — as a designed guarantee seal, not just a sentence.
          </div>
        </div>

        {/* Priority upgrade toggle */}
        <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}>
          <input data-testid="cq-priority-toggle" type="checkbox" checked={co.priority_enabled}
            onChange={(e) => setCoK('priority_enabled', e.target.checked)}
            style={{ accentColor: '#EB258D', width: 16, height: 16 }} />
          <span style={{ fontFamily: 'Inter', fontSize: 14, color: C.icy, fontWeight: 600 }}>⚡ Offer a priority delivery upgrade?</span>
        </label>
        {co.priority_enabled && (
          <div style={{ display: 'grid', gap: 10, padding: '12px 14px', borderRadius: 12, background: 'rgba(235,37,141,0.06)', border: '1px solid rgba(235,37,141,0.28)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr', gap: 10 }}>
              <div>
                <span style={{ ...eyebrow, fontSize: 10 }}>Priority weeks</span>
                <input data-testid="cq-priority-weeks" className="cq-input-focus" type="number" min="0" value={co.priority_weeks}
                  onChange={(e) => setCoK('priority_weeks', e.target.value)}
                  placeholder="e.g. 2" style={inp} />
              </div>
              <div>
                <span style={{ ...eyebrow, fontSize: 10 }}>Priority price</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select data-testid="cq-priority-mode" value={co.priority_mode}
                    onChange={(e) => setCoK('priority_mode', e.target.value)}
                    style={{ ...inp, flex: '0 0 60px' }}>
                    <option value="$">$</option>
                    <option value="percent">%</option>
                  </select>
                  {co.priority_mode === '$' ? (
                    <input data-testid="cq-priority-price" className="cq-input-focus" type="number" min="0" step="1"
                      value={co.priority_price_aud}
                      onChange={(e) => setCoK('priority_price_aud', e.target.value)}
                      placeholder="1500" style={{ ...inp, flex: 1 }} />
                  ) : (
                    <input data-testid="cq-priority-percent" className="cq-input-focus" type="number" min="0" step="0.5"
                      value={co.priority_percent}
                      onChange={(e) => setCoK('priority_percent', e.target.value)}
                      placeholder="15" style={{ ...inp, flex: 1 }} />
                  )}
                </div>
              </div>
              <div style={{ fontFamily: 'Inter', fontSize: 11.5, color: C.muted, lineHeight: 1.5, paddingTop: 20 }}>
                {co.priority_mode === '$'
                  ? `Flat $ added to the total when priority is picked.`
                  : `Percentage of the subtotal added when priority is picked.`}
              </div>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
              <input data-testid="cq-scarcity-toggle" type="checkbox" checked={co.priority_scarcity_enabled}
                onChange={(e) => setCoK('priority_scarcity_enabled', e.target.checked)}
                style={{ accentColor: '#EB258D', width: 14, height: 14 }} />
              <span style={{ fontFamily: 'Inter', fontSize: 12.5, color: C.muted }}>Show a scarcity line under the priority card</span>
            </label>
            {co.priority_scarcity_enabled && (
              <input data-testid="cq-scarcity-text" className="cq-input-focus" value={co.priority_scarcity_text}
                onChange={(e) => setCoK('priority_scarcity_text', e.target.value)}
                placeholder="Limited priority slots available" style={inp} />
            )}
          </div>
        )}

        {/* Pay-in-full discount toggle */}
        <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}>
          <input data-testid="cq-payinfull-toggle" type="checkbox" checked={co.payinfull_enabled}
            onChange={(e) => setCoK('payinfull_enabled', e.target.checked)}
            style={{ accentColor: '#34D1B0', width: 16, height: 16 }} />
          <span style={{ fontFamily: 'Inter', fontSize: 14, color: C.icy, fontWeight: 600 }}>💰 Offer a discount for paying in full upfront?</span>
        </label>
        {co.payinfull_enabled && (
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 10, padding: '12px 14px', borderRadius: 12, background: 'rgba(52,209,176,0.06)', border: '1px solid rgba(52,209,176,0.28)', alignItems: 'end' }}>
            <div>
              <span style={{ ...eyebrow, fontSize: 10 }}>Discount %</span>
              <input data-testid="cq-payinfull-percent" className="cq-input-focus" type="number" min="0" step="0.5"
                value={co.payinfull_discount_percent}
                onChange={(e) => setCoK('payinfull_discount_percent', e.target.value)}
                placeholder="10" style={inp} />
            </div>
            <div style={{ fontFamily: 'Inter', fontSize: 12.5, color: C.muted, lineHeight: 1.55, paddingBottom: 8 }}>
              Client sees <b style={{ color: C.icy }}>&quot;Pay in full and save {co.payinfull_discount_percent || 'N'}%&quot;</b> next to the total. Total updates live if picked; the discount flows into the Xero draft as a negative line item.
            </div>
          </div>
        )}
      </section>
      )}

      <div className="cq-fade cq-fade-4" style={{ display: 'flex', gap: 10 }}>
        <button data-testid="cq-create" className="cq-btn-lift" onClick={save} disabled={busy} style={btnPrimary}>{busy ? '…' : (isEdit ? 'Update quote →' : (mode === 'invoice' ? 'Create invoice + Xero draft →' : 'Save draft →'))}</button>
      </div>
    </div>
  );
};

const STATUS_LABEL = { draft: 'Draft', approved: 'Approved', sent: 'Sent', viewed: 'Viewed', negotiating: 'Negotiating', verbally_agreed: 'Verbally agreed', accepted: 'Accepted', declined: 'Declined', expired: 'Expired',
                        invoice_draft: 'Invoice draft', invoice_sent: 'Invoice sent', invoice_paid: 'Invoice paid', invoice_voided: 'Invoice voided' };
const STATUS_ORDER = ['draft', 'approved', 'sent', 'viewed', 'negotiating', 'verbally_agreed', 'accepted', 'declined', 'expired'];
const INVOICE_STATUS_ORDER = ['draft', 'invoice_draft', 'invoice_sent', 'invoice_paid', 'invoice_voided'];

const StatusMenu = ({ q, anchorRef, onPick, onClose }) => {
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!anchorRef?.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 6, left: r.left });
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) onClose(); };
    const onScroll = () => onClose();
    document.addEventListener('mousedown', close);
    window.addEventListener('resize', onScroll);
    window.addEventListener('scroll', onScroll, true);
    return () => { document.removeEventListener('mousedown', close); window.removeEventListener('resize', onScroll); window.removeEventListener('scroll', onScroll, true); };
  }, [anchorRef, onClose]);
  if (!pos) return null;
  return createPortal(
    <div ref={menuRef} data-testid="cq-status-menu"
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 5000,
               background: C.surface, border: `1px solid ${C.hairBright}`, borderRadius: 12,
               padding: 6, boxShadow: '0 20px 60px rgba(0,0,0,0.6)', minWidth: 200 }}>
      <p style={{ margin: '4px 8px 6px', fontFamily: 'Inter', fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.faint }}>Change status</p>
      {(q.mode === 'invoice' ? INVOICE_STATUS_ORDER : STATUS_ORDER).map((s) => {
        const cur = s === q.status;
        return (
          <button key={s} data-testid={`cq-status-pick-${s}`}
            onClick={(e) => { e.stopPropagation(); if (!cur) onPick(s); }}
            disabled={cur}
            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 10px',
                     background: cur ? 'rgba(255,255,255,0.05)' : 'transparent',
                     border: 'none', borderRadius: 8, cursor: cur ? 'default' : 'pointer',
                     color: cur ? C.icy : STATUS_C[s] || C.icy,
                     fontFamily: 'Inter', fontSize: 12, fontWeight: 700, textAlign: 'left' }}
            onMouseEnter={(e) => { if (!cur) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            onMouseLeave={(e) => { if (!cur) e.currentTarget.style.background = 'transparent'; }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_C[s] || C.faint }} />
            <span style={{ flex: 1 }}>{STATUS_LABEL[s]}</span>
            {cur && <span style={{ color: C.faint, fontSize: 10 }}>current</span>}
          </button>
        );
      })}
    </div>,
    document.body,
  );
};

const Detail = ({ q, say, reload, onBack, onEdit }) => {
  const [sendOpen, setSendOpen] = useState(false);
  const [toEmail, setToEmail] = useState(q.contact_email || '');
  const [introLine, setIntroLine] = useState('');
  const [busy, setBusy] = useState(false);
  const [visionUrl, setVisionUrl] = useState(q.vision?.link_url || '');
  const [visionLabel, setVisionLabel] = useState(q.vision?.link_label || '');
  // Adaptive email copy (v10): headline + intro that bend to whatever
  // the quote actually is (automation, website, branding, consult, app,
  // Claude setup, ops OS). AI generates on first preview open, MJ can
  // edit either field, edits persist on the quote.
  const [headlineLead, setHeadlineLead] = useState(q.email_copy?.headline_lead || '');
  const [headlineBiz, setHeadlineBiz] = useState(q.email_copy?.headline_business || '');
  const [emailIntro, setEmailIntro] = useState(q.email_copy?.intro || '');
  const [copyGenBusy, setCopyGenBusy] = useState(false);
  const [copyDirty, setCopyDirty] = useState(false);
  // Send preview state: { subject, from_display, to, html, quote_public_url,
  //                       has_vision, first_name, business_name, email_copy,
  //                       email_copy_source }
  const [preview, setPreview] = useState(null);
  const [previewTab, setPreviewTab] = useState('email'); // 'email' | 'page'
  const [previewBusy, setPreviewBusy] = useState(false);
  const totals = q.totals || {};
  const act = async (fn, okMsg) => { setBusy(true); try { await fn(); say(okMsg); reload(); } catch (e) { say('Failed: ' + e.message); } setBusy(false); };
  const approve = () => act(() => req('POST', `/api/admin/cq/quotes/${q.quote_id}/approve`), '✓ Approved — hosted page is live');
  const currentCopy = () => ({ headline_lead: headlineLead, headline_business: headlineBiz, intro: emailIntro });
  const send = () => act(async () => {
    await req('POST', `/api/admin/cq/quotes/${q.quote_id}/send`,
              { to_email: toEmail, email_copy: currentCopy() });
    setSendOpen(false); setPreview(null); setCopyDirty(false);
  }, '📧 Sent');
  const rebuildPreviewHtml = async (copy) => {
    const params = new URLSearchParams({
      headline_lead: copy.headline_lead || '',
      headline_business: copy.headline_business || '',
      intro: copy.intro || '',
    }).toString();
    return await req('GET', `/api/admin/cq/quotes/${q.quote_id}/send_preview?${params}`);
  };
  const openPreview = async () => {
    setPreviewBusy(true);
    try {
      // If no copy generated yet (fresh quote or first send), auto-run
      // the AI so MJ opens the preview with real content, not the safe
      // generic fallback. If the quote already has copy (either
      // AI-generated or MJ-edited), reuse it as-is.
      let copy = currentCopy();
      const empty = !copy.headline_lead || !copy.headline_business || !copy.intro;
      if (empty) {
        try {
          const r = await req('POST', `/api/admin/cq/quotes/${q.quote_id}/email_copy_ai`, {});
          copy = r.email_copy || copy;
          setHeadlineLead(copy.headline_lead || '');
          setHeadlineBiz(copy.headline_business || '');
          setEmailIntro(copy.intro || '');
          if (r.used_fallback) say('⚠ AI copywriter unavailable — using safe defaults, edit below');
        } catch (_) { /* preview still opens with whatever we have */ }
      }
      const p = await rebuildPreviewHtml(copy);
      // Sync UI state with what the server actually resolved (in case of
      // partial fallback merging).
      if (p.email_copy) {
        setHeadlineLead(p.email_copy.headline_lead || '');
        setHeadlineBiz(p.email_copy.headline_business || '');
        setEmailIntro(p.email_copy.intro || '');
      }
      setPreview(p); setPreviewTab('email');
    } catch (e) { say('Could not build preview: ' + (e?.message || 'unknown')); }
    setPreviewBusy(false);
  };
  const refreshPreview = async () => {
    setPreviewBusy(true);
    try {
      const p = await rebuildPreviewHtml(currentCopy());
      setPreview(p); setCopyDirty(false);
    } catch (e) { say('Refresh failed: ' + (e?.message || 'unknown')); }
    setPreviewBusy(false);
  };
  const regenerateWithAI = async () => {
    setCopyGenBusy(true);
    try {
      const r = await req('POST', `/api/admin/cq/quotes/${q.quote_id}/email_copy_ai`, {});
      const c = r.email_copy || {};
      setHeadlineLead(c.headline_lead || '');
      setHeadlineBiz(c.headline_business || '');
      setEmailIntro(c.intro || '');
      setCopyDirty(false);
      if (r.used_fallback) {
        say('⚠ AI unavailable — safe defaults loaded, edit below');
      } else {
        say('🪄 Fresh AI copy loaded');
      }
      // Rebuild preview iframe with the new copy
      const p = await rebuildPreviewHtml(c);
      setPreview(p);
    } catch (e) { say('AI regenerate failed: ' + (e?.message || 'unknown')); }
    setCopyGenBusy(false);
  };
  const sendTest = async () => {
    setPreviewBusy(true);
    try {
      const r = await req('POST', `/api/admin/cq/quotes/${q.quote_id}/send_test`,
                            { email_copy: currentCopy() });
      say(`📧 Test sent to ${r.sent_to}`);
    } catch (e) { say('Test-send failed: ' + (e?.message || 'unknown')); }
    setPreviewBusy(false);
  };
  const decline = () => act(() => req('POST', `/api/admin/cq/quotes/${q.quote_id}/status`, { status: 'declined' }), 'Marked declined');
  const [statusOpen, setStatusOpen] = useState(false);
  const statusChipRef = useRef(null);
  const pickStatus = async (target) => {
    setStatusOpen(false);
    const label = STATUS_LABEL[target] || target;
    // Moving AWAY from accepted → use existing revert-acceptance chain
    if (q.status === 'accepted' && target !== 'accepted') {
      const ok = window.confirm(`Change status from Accepted → ${label}?\n\nThis will call the revert-acceptance chain: cancels the linked job and voids the Xero draft.`);
      if (!ok) return;
      await act(async () => {
        const r = await req('POST', `/api/admin/cq/quotes/${q.quote_id}/revert_acceptance`, { confirm: 'REVERT', reason: `manual status change → ${target}` });
        // Now set the actual target status
        await req('POST', `/api/admin/cq/quotes/${q.quote_id}/status`, { status: target });
        say(`Status → ${label} · job cancelled${r.xero?.voided ? ' · Xero draft voided' : ''}`);
      }, '');
      return;
    }
    // Moving TO accepted → double-confirm (spawns job + Xero draft)
    if (target === 'accepted') {
      const ok = window.confirm(`Mark ${q.quote_number} as Accepted?\n\nThis spawns the Job + Xero draft — same as the client accepting the quote.`);
      if (!ok) return;
      await act(() => req('POST', `/api/admin/cq/quotes/${q.quote_id}/status`, { status: target, confirm: true }), `✓ Status → ${label}`);
      return;
    }
    await act(() => req('POST', `/api/admin/cq/quotes/${q.quote_id}/status`, { status: target }), `Status → ${label}`);
  };
  const duplicate = () => act(async () => { const r = await req('POST', `/api/admin/cq/quotes/${q.quote_id}/duplicate`); say('📄 Duplicated'); onBack(); }, '');
  const archive = () => act(() => req('POST', `/api/admin/cq/quotes/${q.quote_id}/archive`), '🗄 Archived · undo from Archive drawer');
  const restore = () => act(() => req('POST', `/api/admin/cq/quotes/${q.quote_id}/restore`), '↺ Restored');
  const destroy = async () => {
    if (!window.confirm(`Permanently delete quote ${q.quote_number}? This cannot be undone.`)) return;
    await act(() => req('DELETE', `/api/admin/cq/quotes/${q.quote_id}?confirm=DELETE`), '🗑 Deleted');
    onBack();
  };
  const revertStatus = () => act(() => req('POST', `/api/admin/cq/quotes/${q.quote_id}/revert_status`), '↺ Reverted to draft');
  const revertAcceptance = async () => {
    const reason = window.prompt('Revert acceptance? Reason (optional):', '');
    if (reason === null) return;
    await act(async () => {
      const r = await req('POST', `/api/admin/cq/quotes/${q.quote_id}/revert_acceptance`, { confirm: 'REVERT', reason });
      if (r.warnings && r.warnings.length) {
        say('⚠ Reverted — ' + r.warnings.join(' · '));
      } else {
        say('↺ Acceptance reverted · job cancelled' + (r.xero?.voided ? ' · Xero draft voided' : ''));
      }
    }, '');
  };
  const advance = (jid) => act(() => req('POST', `/api/admin/cq/jobs/${jid}/advance`), '→ Stage advanced');
  const promiseKept = () => act(() => req('POST', '/api/admin/desk/debrief/promise_mark', { date: q.promise_suggestion.date, promise_id: q.promise_suggestion.promise_id, action: 'done' }), '🏆 Promise marked kept');
  const uploadVision = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append('file', f);
    await act(() => req('POST', `/api/admin/cq/quotes/${q.quote_id}/vision/image`, fd, true), '🖼 Mockup added');
  };
  const saveVisionLink = () => act(() => req('PATCH', `/api/admin/cq/quotes/${q.quote_id}`, { vision_link: { url: visionUrl, label: visionLabel } }), '✓ Vision link saved');
  const sc = STATUS_C[q.status] || C.faint;

  return (
    <div data-testid="cq-detail" style={{ display: 'grid', gap: 16 }}>
      <div className="cq-fade" style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
        <button data-testid="cq-detail-back" onClick={onBack} style={ghost}>← quotes</button>
        <div style={{ flex: 1, minWidth: 220 }}>
          <span style={eyebrow}>{q.quote_number}</span>
          <h1 style={{ ...hFont, margin: 0, fontSize: 26, letterSpacing: '-0.01em' }}>
            <span style={{ backgroundImage: SPECTRUM, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{q.business_name}</span>
          </h1>
        </div>
        <button ref={statusChipRef} data-testid="cq-detail-status"
          onClick={() => setStatusOpen(!statusOpen)}
          title="Click to change status"
          style={{ ...chip(sc), border: 'none', cursor: 'pointer',
                   background: `${sc}18`, padding: '5px 10px 5px 10px' }}>
          {q.status} <span style={{ opacity: 0.55, fontSize: 9, marginLeft: 4 }}>▾</span>
        </button>
        {statusOpen && <StatusMenu q={q} anchorRef={statusChipRef} onPick={pickStatus} onClose={() => setStatusOpen(false)} />}
      </div>

      {q.promise_suggestion && (
        <div data-testid="cq-promise-suggestion" className="cq-fade" style={{ ...card, borderLeft: '3px solid #FFD37D', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 13, color: C.icy, flex: 1, minWidth: 200 }}>⚡ Debrief promise to <b>{q.promise_suggestion.person_name}</b>: “{q.promise_suggestion.what}” — mark it kept?</p>
          <button data-testid="cq-promise-kept" onClick={promiseKept} style={{ ...btnPrimary, backgroundImage: 'none', background: '#FFD37D', color: '#100838', boxShadow: '0 8px 24px rgba(255,211,125,0.25)' }}>✓ Kept</button>
        </div>
      )}

      <section className="cq-fade cq-fade-2" style={{ ...card, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {q.mode === 'invoice' ? (
          <>
            {/* Invoice mode — no hosted page, no send. Xero is the sender. */}
            {q.xero_invoice_number
              ? <span data-testid="cq-invoice-xero-badge" style={{ ...chip('#F0C36D'), padding: '5px 12px' }}>🧾 Xero draft {q.xero_invoice_number}</span>
              : q.xero_error
                ? <span data-testid="cq-invoice-xero-error" style={{ ...chip('#FF8FC1'), padding: '5px 12px' }}>⚠ Xero draft failed — check connection</span>
                : <span data-testid="cq-invoice-nodraft" style={{ ...chip(C.faint), padding: '5px 12px' }}>Xero draft pending</span>}
            <p data-testid="cq-invoice-flow-note" style={{ margin: '0 0 0 6px', fontFamily: 'Inter', fontSize: 11.5, color: C.muted, flex: 1, minWidth: 200 }}>
              Approve + send from Xero. This system tracks the record; Xero owns the send.
            </p>
            {!q.archived && <button data-testid="cq-detail-edit" onClick={onEdit}
              style={{ ...ghost, color: '#34D1B0', borderColor: 'rgba(52,209,176,0.5)',
                       background: 'rgba(52,209,176,0.06)', fontWeight: 700 }}>✏ Edit invoice</button>}
          </>
        ) : (
          <>
            {q.status !== 'accepted' && q.status !== 'accepted_pending' && !q.archived && (
              <button data-testid="cq-detail-edit" onClick={onEdit}
                style={{ ...ghost, color: '#34D1B0', borderColor: 'rgba(52,209,176,0.5)',
                         background: 'rgba(52,209,176,0.06)', fontWeight: 700 }}>
                ✏ Edit quote
              </button>
            )}
            {q.status === 'draft' && <button data-testid="cq-approve" className="cq-btn-lift" onClick={approve} disabled={busy} style={btnPrimary}>✓ Approve — publish hosted page</button>}
            {q.status !== 'draft' && (
              <>
                <a data-testid="cq-public-link" href={q.public_url} target="_blank" rel="noreferrer" style={{ ...ghost, textDecoration: 'none', color: '#34D1B0', borderColor: 'rgba(52,209,176,0.4)' }}>↗ hosted page</a>
                <button data-testid="cq-copy-link" onClick={() => { navigator.clipboard.writeText(q.public_url); say('Link copied'); }} style={ghost}>⧉ copy link</button>
              </>
            )}
            {['approved', 'sent', 'viewed'].includes(q.status) && <button data-testid="cq-send-open" className="cq-btn-lift" onClick={() => setSendOpen(!sendOpen)} style={btnPrimary}>📧 Send{q.sent_at ? ' again' : ''}</button>}
            {['sent', 'viewed'].includes(q.status) && <button data-testid="cq-decline" onClick={decline} style={{ ...ghost, color: '#FF8FC1', borderColor: 'rgba(235,37,141,0.35)' }}>mark declined</button>}
            {['sent', 'viewed'].includes(q.status) && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Inter', fontSize: 12, color: C.muted }}>
                <input data-testid="cq-followups-toggle" type="checkbox" checked={q.followups_enabled !== false}
                  onChange={(e) => act(() => req('PATCH', `/api/admin/cq/quotes/${q.quote_id}`, { followups_enabled: e.target.checked }), e.target.checked ? 'Auto follow-ups ON' : 'Auto follow-ups OFF')}
                  style={{ accentColor: '#34D1B0' }} /> auto follow-ups
              </label>
            )}
            {['sent', 'approved'].includes(q.status) && (q.view_count || 0) === 0 && !q.first_viewed_at && (
              <button data-testid="cq-revert-status" onClick={revertStatus} style={ghost}>↺ back to draft</button>
            )}
            {q.status === 'accepted' && (
              <button data-testid="cq-revert-acceptance" onClick={revertAcceptance}
                style={{ ...ghost, color: '#FFD37D', borderColor: 'rgba(255,211,125,0.4)' }}>↺ undo acceptance</button>
            )}
          </>
        )}
      </section>

      <section className="cq-fade cq-fade-3" style={{ ...card, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', background: 'rgba(255,255,255,0.02)' }}>
        <span style={{ ...eyebrow, marginRight: 4 }}>Actions</span>
        <button data-testid="cq-duplicate" onClick={duplicate} style={ghost}>📄 Duplicate</button>
        {!q.archived && <button data-testid="cq-archive" onClick={archive} style={ghost}>🗄 Archive</button>}
        {q.archived && <button data-testid="cq-restore" onClick={restore} style={ghost}>↺ Restore</button>}
        {q.archived && (
          <button data-testid="cq-delete" onClick={destroy}
            style={{ ...ghost, color: '#FF8FC1', borderColor: 'rgba(235,37,141,0.35)' }}>🗑 Delete forever</button>
        )}
      </section>

      {sendOpen && !preview && (
        <section data-testid="cq-send-modal" className="cq-fade" style={card}>
          <span style={eyebrow}>To</span>
          <input data-testid="cq-send-to" className="cq-input-focus" value={toEmail} onChange={(e) => setToEmail(e.target.value)} style={inp} />
          <span style={{ ...eyebrow, marginTop: 12 }}>Intro line (editable per quote)</span>
          <textarea data-testid="cq-send-intro" className="cq-input-focus" rows={3} placeholder={q.intro || `Here's the quote we talked about for ${q.business_name} — everything laid out on a private page. Take your time, and just reply if anything's off.`} value={introLine} onChange={(e) => setIntroLine(e.target.value)} style={{ ...inp, resize: 'vertical' }} />
          <p style={{ margin: '10px 0 6px', fontFamily: 'Inter', fontSize: 11, color: C.faint, letterSpacing: '0.03em' }}>
            You can&rsquo;t send blind — the next step opens a preview of exactly what {q.contact_first_name || 'they'} will receive.
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button data-testid="cq-send-preview" className="cq-btn-lift" onClick={openPreview} disabled={previewBusy} style={btnPrimary}>
              {previewBusy ? '…' : 'Preview send →'}
            </button>
            <button data-testid="cq-send-cancel" onClick={() => setSendOpen(false)} style={ghost}>cancel</button>
          </div>
        </section>
      )}

      {preview && (
        <section data-testid="cq-preview-modal" className="cq-fade"
          style={{ ...card, padding: 0, overflow: 'hidden', background: C.surface }}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.hair}`, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <span style={eyebrow}>Preview send · {preview.quote_number}</span>
              <p style={{ ...hFont, margin: '4px 0 0', fontSize: 17 }}>
                <span style={{ color: C.faint, fontFamily: 'Inter', fontWeight: 500, fontSize: 12 }}>Subject:</span> {preview.subject}
              </p>
              <p style={{ margin: '3px 0 0', fontFamily: 'Inter', fontSize: 12, color: C.faint }}>
                From <b style={{ color: C.muted }}>{preview.from_display}</b> → {toEmail || preview.to || <i>no recipient</i>}
              </p>
            </div>
            <button data-testid="cq-preview-close" onClick={() => setPreview(null)} style={ghost}>← back to compose</button>
          </div>
          <div style={{ display: 'flex', gap: 4, padding: '10px 20px', background: 'rgba(220,240,245,0.02)', borderBottom: `1px solid ${C.hair}` }}>
            {[['email', '✉ Email — what they receive'], ['page', '🖥 Quote page — what they see']].map(([k, label]) => (
              <button key={k} data-testid={`cq-preview-tab-${k}`} onClick={() => setPreviewTab(k)}
                style={{ padding: '7px 14px', borderRadius: 8, cursor: 'pointer', border: 'none',
                         background: previewTab === k ? 'rgba(124,92,246,0.18)' : 'transparent',
                         color: previewTab === k ? C.icy : C.muted,
                         fontFamily: 'Inter', fontSize: 12.5, fontWeight: 600 }}>{label}</button>
            ))}
          </div>

          {/* Adaptive email-copy editor — v10. The words bend to whatever
              this quote actually is. AI generates on open; MJ edits either
              field; refresh iframe on demand. */}
          <div data-testid="cq-preview-copy-editor" style={{ padding: '14px 20px 12px', borderBottom: `1px solid ${C.hair}`, background: 'rgba(124,92,246,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
              <span style={{ ...eyebrow, margin: 0 }}>
                Email copy · {preview.email_copy_source === 'edited' ? 'edited by you' :
                              preview.email_copy_source === 'ai' ? 'AI-generated' : 'safe defaults'}
              </span>
              <div style={{ flex: 1 }} />
              <button data-testid="cq-copy-regenerate" onClick={regenerateWithAI} disabled={copyGenBusy}
                style={{ ...ghost, color: '#7C5CF6', borderColor: 'rgba(124,92,246,0.4)', fontWeight: 700 }}>
                {copyGenBusy ? '…' : '🪄 Regenerate with AI'}
              </button>
              {copyDirty && (
                <button data-testid="cq-copy-refresh" onClick={refreshPreview} disabled={previewBusy}
                  style={{ ...ghost, color: '#34D1B0', borderColor: 'rgba(52,209,176,0.4)', fontWeight: 700 }}>
                  {previewBusy ? '…' : '↻ Refresh preview'}
                </button>
              )}
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              <label style={{ display: 'grid', gap: 4 }}>
                <span style={{ fontFamily: 'Inter', fontSize: 11, color: C.faint, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Headline — white lead</span>
                <input data-testid="cq-copy-lead" className="cq-input-focus"
                  value={headlineLead}
                  onChange={(e) => { setHeadlineLead(e.target.value); setCopyDirty(true); }}
                  placeholder="e.g. Here's how we'd automate"
                  style={{ ...inp }} />
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span style={{ fontFamily: 'Inter', fontSize: 11, color: C.faint, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Headline — gradient span (usually business name + full stop)</span>
                <input data-testid="cq-copy-biz" className="cq-input-focus"
                  value={headlineBiz}
                  onChange={(e) => { setHeadlineBiz(e.target.value); setCopyDirty(true); }}
                  placeholder={`${q.business_name}.`}
                  style={{ ...inp }} />
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span style={{ fontFamily: 'Inter', fontSize: 11, color: C.faint, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Intro paragraph (1–2 sentences)</span>
                <textarea data-testid="cq-copy-intro" className="cq-input-focus" rows={3}
                  value={emailIntro}
                  onChange={(e) => { setEmailIntro(e.target.value); setCopyDirty(true); }}
                  placeholder="One or two sentences describing THIS specific quote…"
                  style={{ ...inp, resize: 'vertical' }} />
              </label>
            </div>
            {copyDirty && (
              <p data-testid="cq-copy-dirty" style={{ margin: '10px 0 0', fontFamily: 'Inter', fontSize: 11.5, color: '#FFD37D' }}>
                ✎ Unsaved edits — click <b>Refresh preview</b> to re-render, or Send / Send test to apply.
              </p>
            )}
          </div>

          <div style={{ background: '#0D0724', minHeight: 400, maxHeight: 720, overflow: 'auto' }}>
            {previewTab === 'email' && (
              <iframe data-testid="cq-preview-email" title="email-preview" srcDoc={preview.html}
                style={{ width: '100%', minHeight: 720, border: 'none', background: '#0D0724' }} />
            )}
            {previewTab === 'page' && (
              <iframe data-testid="cq-preview-page" title="page-preview" src={preview.quote_public_url}
                style={{ width: '100%', minHeight: 720, border: 'none', background: '#0B0421' }} />
            )}
          </div>
          <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.hair}`, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button data-testid="cq-send-test" onClick={sendTest} disabled={previewBusy}
              style={{ ...ghost, color: '#FFD37D', borderColor: 'rgba(255,211,125,0.4)' }}>
              {previewBusy ? '…' : '📧 Send test to me'}
            </button>
            <div style={{ flex: 1 }} />
            <button data-testid="cq-preview-send" className="cq-btn-lift" onClick={send} disabled={busy || !toEmail || toEmail.indexOf('@') === -1}
              style={btnPrimary}>
              {busy ? '…' : `Send to ${toEmail || 'client'} →`}
            </button>
          </div>
        </section>
      )}

      <section className="cq-fade cq-fade-2" style={card}>
        <span style={eyebrow}>Your Vision — mockups & preview link (optional)</span>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {(q.vision?.images || []).map((im) => (
            <div key={im.img_id} style={{ position: 'relative' }}>
              <img src={`${API}/api/cq/${q.token}/vision/${im.img_id}`} alt="" style={{ width: 116, height: 78, objectFit: 'cover', borderRadius: 12, border: `1px solid ${C.hairBright}` }} />
              <button data-testid={`cq-vision-del-${im.img_id}`} onClick={() => act(() => req('DELETE', `/api/admin/cq/quotes/${q.quote_id}/vision/image/${im.img_id}`), 'Removed')} style={{ position: 'absolute', top: -7, right: -7, width: 20, height: 20, borderRadius: 999, border: 'none', background: '#EB258D', color: '#fff', fontSize: 11, cursor: 'pointer' }}>✕</button>
            </div>
          ))}
          <label data-testid="cq-vision-upload" style={{ ...ghost, cursor: 'pointer' }}>
            + mockup image<input type="file" accept="image/*" onChange={uploadVision} style={{ display: 'none' }} />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <input data-testid="cq-vision-url" className="cq-input-focus" placeholder="Preview link (Figma / demo app URL)" value={visionUrl} onChange={(e) => setVisionUrl(e.target.value)} style={{ ...inp, flex: 2, minWidth: 200 }} />
          <input data-testid="cq-vision-label" className="cq-input-focus" placeholder="Label — e.g. Explore the interactive concept" value={visionLabel} onChange={(e) => setVisionLabel(e.target.value)} style={{ ...inp, flex: 1, minWidth: 170 }} />
          <button data-testid="cq-vision-save" onClick={saveVisionLink} style={ghost}>save</button>
        </div>
        {(q.vision?.engagement || 0) > 0 && <p data-testid="cq-vision-signal" style={{ margin: '12px 0 0', fontFamily: 'Inter', fontSize: 12.5, color: '#34D1B0', fontWeight: 700 }}>👀 explored the vision ×{q.vision.engagement}</p>}
      </section>

      <section className="cq-fade cq-fade-3" style={card}>
        <span style={eyebrow}>Line items</span>
        {(q.line_items || []).map((li, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, padding: '12px 14px', margin: '0 0 10px', background: 'rgba(220,240,245,0.04)', border: `1px solid ${C.hair}`, borderLeft: `3px solid ${ACCENTS[i % ACCENTS.length]}`, borderRadius: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <p style={{ ...hFont, margin: 0, fontSize: 14.5 }}>{li.title}</p>
              <p style={{ margin: '4px 0 0', fontFamily: 'Inter', fontSize: 12, lineHeight: 1.6, color: C.muted }}>{li.description}</p>
            </div>
            <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>{li.qty} × {money(li.unit_price)}{li.gst ? ' +GST' : ''} = <b style={{ color: C.icy, fontFamily: 'Poppins, Inter', fontSize: 14 }}>{money(li.amount)}</b></p>
          </div>
        ))}
        <p style={{ margin: '14px 0 0', textAlign: 'right', fontFamily: 'Inter', fontSize: 13, color: C.faint }}>
          Total inc GST <b style={{ fontFamily: 'Poppins, Inter', fontWeight: 700, fontSize: 24, marginLeft: 8, backgroundImage: SPECTRUM, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{money(totals.total_inc)}</b>
        </p>
      </section>

      {(q.external_services || []).length > 0 && (
        <section data-testid="cq-detail-ext" className="cq-fade cq-fade-3" style={{ ...card, borderLeft: '3px solid #1FB6C1', background: 'rgba(31,182,193,0.04)' }}>
          <span style={eyebrow}>External services — running costs (client sees this on the hosted page)</span>
          <p style={{ margin: '2px 0 12px', fontFamily: 'Inter', fontSize: 11.5, color: C.faint }}>Excluded from Xero. Separate from the TravelTrucks total the client accepts.</p>
          {(q.external_services || []).map((x, i) => (
            <div key={i} data-testid={`cq-detail-ext-${i}`} style={{ display: 'flex', gap: 12, padding: '10px 12px', margin: '0 0 8px', background: 'rgba(220,240,245,0.03)', border: `1px solid ${C.hair}`, borderRadius: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <p style={{ ...hFont, margin: 0, fontSize: 13.5 }}>{x.name} {!x.required && <span style={{ color: C.faint, fontFamily: 'Inter', fontWeight: 500, fontSize: 11 }}>· optional</span>}</p>
                {x.purpose && <p style={{ margin: '3px 0 0', fontFamily: 'Inter', fontSize: 11.5, color: C.muted, lineHeight: 1.55 }}>{x.purpose}</p>}
                <p style={{ margin: '3px 0 0', fontFamily: 'Inter', fontSize: 10.5, color: C.faint }}>Billed by: {x.billed_by === 'via_kindra' ? 'Via TravelTrucks' : 'Third party directly'}</p>
              </div>
              <p style={{ margin: 0, fontFamily: 'Poppins, Inter', fontSize: 14, fontWeight: 600, color: '#1FB6C1', whiteSpace: 'nowrap' }}>~${Number(x.est_cost_aud || 0).toLocaleString()}{x.period === 'mo' ? '/mo' : x.period === 'yr' ? '/yr' : ' one-off'}</p>
            </div>
          ))}
        </section>
      )}

      {q.status === 'accepted' && (
        <section data-testid="cq-accepted-box" className="cq-fade" style={{ ...card, borderLeft: '3px solid #34D1B0', background: 'rgba(52,209,176,0.06)' }}>
          <p style={{ margin: 0, fontFamily: 'Poppins, Inter', fontWeight: 600, fontSize: 15, color: '#34D1B0' }}>✅ Accepted by {q.accepted_name} · {new Date(q.accepted_at).toLocaleString('en-AU')}</p>
          <p style={{ margin: '8px 0 0', fontFamily: 'Inter', fontSize: 12.5, color: C.muted }}>
            {q.xero_invoice_number ? `Xero DRAFT invoice ${q.xero_invoice_number} created — send it from Xero.` : q.xero_error ? `⚠ Xero draft failed: ${q.xero_error}` : 'Xero draft pending…'}
          </p>
          {q.job && (
            <div data-testid="cq-job" style={{ marginTop: 14 }}>
              <span style={eyebrow}>{q.job.title}</span>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
                {['deposit', 'in_progress', 'review', 'delivered', 'invoiced'].map((s, i2) => {
                  const cur = ['deposit', 'in_progress', 'review', 'delivered', 'invoiced'].indexOf(q.job.stage);
                  return <span key={s} style={{ ...chip(i2 <= cur ? '#34D1B0' : C.faint), opacity: i2 <= cur ? 1 : 0.45 }}>{s.replace('_', ' ')}</span>;
                })}
                {q.job.stage !== 'invoiced' && <button data-testid="cq-job-advance" onClick={() => advance(q.job.job_id)} style={{ ...ghost, color: '#34D1B0', borderColor: 'rgba(52,209,176,0.4)' }}>advance →</button>}
              </div>
            </div>
          )}
        </section>
      )}

      <section className="cq-fade cq-fade-4" style={card}>
        <span style={eyebrow}>Timeline</span>
        <div className="cq-scroll" style={{ maxHeight: 260, overflowY: 'auto' }}>
          {[...(q.events || [])].reverse().map((e, i) => (
            <p key={i} data-testid={`cq-event-${i}`} style={{ margin: '0 0 7px', fontFamily: 'Inter', fontSize: 12, color: C.muted }}>
              <span style={{ color: C.faint }}>{new Date(e.ts).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              {' · '}<b style={{ color: e.type === 'accept' ? '#34D1B0' : e.type.startsWith('vision') ? '#1FB6C1' : C.icy }}>{e.type.replace('_', ' ')}</b>
              {e.meta?.to ? ` → ${e.meta.to}` : ''}{e.meta?.variant ? ` (${e.meta.variant})` : ''}{e.meta?.name ? ` — ${e.meta.name}` : ''}
            </p>
          ))}
        </div>
      </section>
    </div>
  );
};
